// Shared by the backend tracer and the frontend "Get API" button, so keep it
// free of node-only imports.
import type { EvmV2Network } from "../blockchains/evmV2Networks.js"

// Bitquery v2 reports a chain's native coin as currency SmartContract "0x".
export const NATIVE_SMART_CONTRACT = "0x"

const ZERO_ADDRESS = "0000000000000000000000000000000000000000"
const HEX_ADDRESS = /^(0x)?[0-9a-f]{40}$/

// Every way a flow or a caller names the native coin: its symbol(s), "-" (v1
// address stats), and the zero address coinpath.state stores it under.
export const nativeCurrencyAliases = (network: EvmV2Network) => new Set([
    ...[network.nativeSymbol, ...(network.nativeAliases ?? [])].map((symbol) => symbol.toLowerCase()),
    "-", "0x", ZERO_ADDRESS, "0x" + ZERO_ADDRESS,
])

/**
 * Money flow `currency` filter → v2 `Currency.SmartContract` values.
 * Flows store a token as its contract address and the native coin as its
 * symbol, e.g. "ETH" or "BNB" (v1 address stats listed native with address
 * "-"), so the symbol must map to native "0x" — never to a symbol match, as
 * plenty of spam tokens are also called "ETH". Anything that is neither native
 * nor an address is dropped.
 */
export function toV2CurrencyFilter(currency: string[] | undefined, network: EvmV2Network): string[] {
    const nativeAliases = nativeCurrencyAliases(network)
    const result = new Set<string>()
    for (const raw of currency ?? []) {
        const value = raw.trim().toLowerCase()
        if (nativeAliases.has(value)) {
            result.add(NATIVE_SMART_CONTRACT)
        } else if (HEX_ADDRESS.test(value)) {
            result.add(value.startsWith("0x") ? value : "0x" + value)
        }
    }
    return [...result]
}

// A flow's date range arrives as a zone-less string on create
// ("2026-10-14T00:00:00") and as the Date Mongo stored it as on later traces.
// Parse the string the way Mongo's cast did (`new Date`), so every trace of a
// flow uses the same window. Returns the RFC 3339 form v2 `DateTime` takes.
export function toUtcDateTime(value: string | Date | undefined): string | undefined {
    if (!value) return undefined
    const date = value instanceof Date ? value : new Date(value)
    return isNaN(date.getTime()) ? undefined : date.toISOString().replace(/\.\d{3}Z$/, "Z")
}
